import { Gender } from './common';
import {
  MemberCardInfo,
  StoreRef,
  TimeLimitEnabled,
  TimeRule,
  UseStoreScopeType,
} from './member-card';
import { OrderPayType } from './order';
import type { FilterRule } from '@/services/member';

// 用户类型
export enum UserType {
  DEFAULT = 0, // 默认
  ADULT = 1, // 成人
  CHILD = 2, // 儿童
}

// 会员信息
export type MemberInfo = {
  user_type: UserType; // 用户类型
  user_id: number; // 用户 ID
  name: string; // 用户姓名
  gender: Gender; // 性别：0 - 未知，1 - 男，2 - 女
  avatar: string; // 头像
  is_member: number; // 是否是会员，0 - 否，1有效会员，2无效会员
  source_type: number; // 来源类型，0 - 默认
  merchant_id: number; // 商户 ID
  store_id: number; // 门店 ID
  store_name: string; // 门店名称
  admin_id: number; // 管理员 ID
  admin_name: string; // 管理员姓名
  phone_number: string; // 手机号码
  app_id: string; // 应用 ID
  open_id: string; // 微信 open ID
  union_id: string; // 微信 union ID
  birth_date: string; // 出生日期
  related_lead: string; // 关联线索
  remarks: string; // 备注信息
  remain_card_num: number; // 剩余卡片数量
  total_card_num: number; // 总卡片数量
  total_used_class_num: number; // 总使用的课次数量
  first_card_time: string; // 首次办卡时间
  card_exp_time: string; // 卡片过期时间
  latest_study_time: string; // 最新学习时间
  latest_manual_follow_time: string; // 最新手动跟进时间
  next_follow_up_time: string; // 下次跟进时间
  create_time: string; // 记录创建时间
  assign_time?: string; // 分配时间

  lead_id: number; // 线索 ID
  user_name: string; // 用户姓名
  source_name: string; // 来源名称
  wx_number: string; // 微信号码
  image_url?: string; // 图片附件
  intention_type: CardIntentionType; // 购卡意向
  is_members?: boolean; // 是否是会员
  id?: number; // 用户 ID
  sub_account_list?: SubAccountInfo[]; // 子账号列表
  account_type: number; // 账号类型 1 表示主账号，2 表示子账号
  class_list?: ClassInfo[]; // 班级列表

  // 用户归属门店对应的收款账户
  payment_config?: {
    id: number;
    entity_name: string;
  };
};

// 班级信息
export type ClassInfo = {
  id: number;
  name: string;
};

// 子账号信息
export type SubAccountInfo = {
  id: number;
  name: string;
  user_id: number;
  user_type: number;
  account_type: number;
};

// 会员卡激活方式
export enum ActivationMode {
  IMMEDIATELY = 1, // 立即激活
  AUTO_ACTIVATE = 2, // 到期自动激活
}

// 会员卡状态
export enum MemberCardStatus {
  ACTIVATED = 1, // 已激活
  INACTIVATED = 2, // 未激活
  STOPPED = 3, // 已停卡
  INVALID = 4, // 已失效
  REFUNDED = 5, // 已退卡
  VOIDED = 6, // 已作废
}

// 会员卡过期清零结算状态
export enum ExpireSettleStatus {
  Unsettled = 1, // 未结算
  Settled = 2, // 已结算
}

// 会员状态
export enum MemberStatus {
  NEVER = 0, // 不曾是会员
  VALID = 1, // 有效会员
  INVALID = 2, // 无效会员
}

// 会员卡类型 主账号/子账号
export enum AccountType {
  MAIN = 1, // 主账号
  SUB = 2, // 子账号
}

// 预约频次限制
export enum AppointmentFrequencyLimit {
  NO_LIMIT = 0, // 无限制
  LIMIT_DAILY = 1, // 限制每天预约次数
  LIMIT_WEEKLY = 2, // 限制每周预约次数
  LIMIT_MONTHLY = 3, // 限制每月预约次数
}

// 操作用户会员卡的动作类型
export enum UserMemberCardActionType {
  CREATE = 1, // 开卡
  ACTIVATE = 2, // 激活
  STOP = 3, // 停卡
  RESUME = 4, // 恢复
  MODIFY_VALIDITY_PERIOD = 5, // 修改有效期
  PASSIVE_INVALID = 6, // 被动失效
  REFUND = 7, // 退卡
  MODIFY_OWNER = 8, // 修改负责人
  MODIFY_ACTIVATION_DATE = 9, // 修改激活日期
  RecoverExpired = 10, // 恢复过期卡
}

// 购卡意向
export enum CardIntentionType {
  UNKNOWN = 1, // 未知
  CONSIDERING = 2, // 考虑中
  INTENTION = 3, // 有意向
  NO_INTENTION = 4, // 已拒绝
}

export const memberCardStatusMap = {
  [MemberCardStatus.ACTIVATED]: {
    color: 'green',
    text: '已激活',
  },
  [MemberCardStatus.INACTIVATED]: {
    color: 'default',
    text: '未激活',
  },
  [MemberCardStatus.STOPPED]: {
    color: 'blue',
    text: '已停卡',
  },
  [MemberCardStatus.INVALID]: {
    color: 'orange',
    text: '已失效',
  },
  [MemberCardStatus.REFUNDED]: {
    color: 'red',
    text: '已退卡',
  },
  [MemberCardStatus.VOIDED]: {
    color: 'default',
    text: '已作废',
  },
};

// 用户会员卡信息
export type UserMemberCardInfo = {
  id: number; // 开卡记录的唯一标识 ID
  user_id: number; // 关联用户表的用户 ID
  member_card_id: number; // 关联会员卡表的会员卡 ID
  actual_amount: number; // 实收金额，单位为分
  pay_image: string; // 付款凭证的图片文件名或存储路径
  activate_type: number; // 激活方式，1 表示立即激活，2 表示约课或到期自动激活
  auto_activate_days: number; // 自动激活的天数
  activate_time: string; // 会员卡的激活时间
  exp_time: string; // 会员卡的失效时间
  total_card_num: number; // 会员卡的总次数
  remain_card_num: number; // 会员卡剩余的次数
  used_class_num: number; // 已经使用的课次数量
  valid_days: number; // 会员卡的有效天数
  status: number; // 会员卡的状态，1 表示已激活，2 表示未激活，3 表示已停卡，4 表示已失效，5 表示已退卡，6 表示已作废
  expire_settle_status: ExpireSettleStatus; // 过期清零结算状态
  expire_settle_time: string; // 过期清零结算时间
  card_issuing_time: string; // 会员卡的办卡时间
  issuing_store_id: number; // 开卡门店的 ID
  issuing_store_name: string; // 开卡门店的名称
  issuing_admin_id: number; // 开卡负责人的 ID
  issuing_admin_name: string; // 开卡负责人的姓名
  member_card_info: MemberCardInfo; // 会员卡的详细信息
  issuing_remarks: string; // 开卡时的备注信息
  stop_time: string; // 会员卡的停卡时间
  stop_days: number; // 停卡的天数
  return_time: string; // 会员卡的退卡时间
  return_amount: number; // 退卡时退还的金额
  create_time: string; // 该条开卡记录的创建时间
  remain_amount: number; // 剩余金额
  order_pay_type: OrderPayType; // 支付方式
  order_no: string; // 订单编号
  order_time: string; // 订单时间
  phone_number: string; // 手机号码
  name: string; // 用户姓名
  use_store_scope_type?: UseStoreScopeType; // 适用门店范围（开卡快照）
  use_stores?: StoreRef[]; // 指定门店（开卡快照）
  time_limit_enabled?: TimeLimitEnabled; // 是否启用可用时段（开卡快照）
  time_rules?: TimeRule[]; // 可用时段规则（开卡快照）
  book_limit_type?: number; // 约课频次限制类型（开卡快照）
  book_limit?: number; // 约课频次限制值（开卡快照）
  book_limit_booked?: number; // 当前周期已预约节数
};

// 查询条件项
export type QueryConditionItem = {
  desc: string;
  req: {
    filter: FilterRule;
  };
};

// 会员统计
export type MemberStatistic = {
  monthly_class_stats: {
    no_class: number; // 本月办卡未消课的会员数量
    class_1_to_5: number; // 本月办卡且消课次数在 1 - 5 次的会员数量
    class_6_to_10: number; // 本月办卡且消课次数在 6 - 10 次的会员数量
    class_11_to_20: number; // 本月办卡且消课次数在 11 - 20 次的会员数量
  };
  activity_stats: {
    active_members: number; // 处于活跃期的会员数量
    quiet_members: number; // 处于沉默期的会员数量
    sleeping_members: number; // 处于睡眠期的会员数量
    lost_members: number; // 处于流失期的会员数量
  };
  renewal_warning: {
    remaining_under_10: number; // 会员卡剩余次数不足 10 次的会员数量
    remaining_under_20: number; // 会员卡剩余次数不足 20 次的会员数量
    remaining_under_30: number; // 会员卡剩余次数不足 30 次的会员数量
    expire_in_10_days: number; // 会员卡将在 10 天内到期的会员数量
    expire_in_30_days: number; // 会员卡将在 30 天内到期的会员数量
    expire_in_60_days: number; // 会员卡将在 60 天内到期的会员数量
    expire_after_10_days: number; // 近10天失效的会员
    expire_after_30_days: number; // 近30天失效的会员
    expire_after_60_days: number; // 近60天失效的会员
  };
  renew_follow_up_filter: {
    renew_unfollowed: number; // 满足续卡要求，且购卡意向=未知
    renew_considering: number; // 满足续卡要求，且购卡意向=考虑中
    renew_interested: number; // 满足续卡要求，且购卡意向=有意向
    renew_todo_follow: number; // 满足续卡要求，且下次跟进时间 <= 今天(自然日结束时间)
  };
  query_conditions: {
    activity_stats: {
      active_members: QueryConditionItem;
      quiet_members: QueryConditionItem;
      sleeping_members: QueryConditionItem;
      lost_members: QueryConditionItem;
    };
    monthly_class_stats: {
      no_class: QueryConditionItem;
      class_1_to_5: QueryConditionItem;
      class_6_to_10: QueryConditionItem;
      class_11_to_20: QueryConditionItem;
    };
    renewal_warning: {
      remaining_under_10: QueryConditionItem;
      remaining_under_20: QueryConditionItem;
      remaining_under_30: QueryConditionItem;
      expire_in_10_days: QueryConditionItem;
      expire_in_30_days: QueryConditionItem;
      expire_in_60_days: QueryConditionItem;
      expire_after_10_days: QueryConditionItem;
      expire_after_30_days: QueryConditionItem;
      expire_after_60_days: QueryConditionItem;
    };
    renew_follow_up_filter: {
      renew_unfollowed: QueryConditionItem;
      renew_considering: QueryConditionItem;
      renew_interested: QueryConditionItem;
      renew_todo_follow: QueryConditionItem;
    };
  };
};

//会员卡开卡列表
export type MemberCardOpenCardList = {
  total: number;
  list: UserMemberCardInfo[];
};

// 搜索用户信息
export type SearchUserInfo = {
  user_id: number; // 用户 ID
  name: string; // 用户姓名
  phone_number: string; // 手机号码
  avatar: string; // 头像
  wx_number: string; // 微信号码
  is_member: MemberStatus; // 是否是会员 1会员 2过期会员 0, 非会员
  admin_id: number; // 管理员 ID
  admin_name: string; // 管理员姓名
  store_id: number; // 门店 ID
  store_name: string; // 门店名称
  remarks: string; // 备注信息
};

// 会员开卡列表
export type MemberCardList = {
  total: number;
  list: UserMemberCardInfo[];
};

// 用户类型
export enum UserStageType {
  USER = 1, // 用户
  LEADS = 2, // 线索
}

// 会员卡状态
export enum MemberCardCheckStatus {
  All = 0, // 不限
  New = 1, // 新开卡
  Renewal = 2, // 续卡
  Transfer = 3, // 接转卡
}

// 会员卡批量延期操作记录
export type MemberCardExtendRecord = {
  id: number; // 延期记录 ID

  reason: string; // 延期原因
  extend_days: number; // 延期天数

  affected_cards: number; // 受影响的会员卡数量
  affected_users: number; // 受影响的用户数量

  member_cards:
    | {
        member_card_id: number;
        member_card_name: string;
      }[]
    | null; // 具体会员卡列表（未返回时为 null）

  store_id: number; // 门店 ID（0=全部门店）
  store_name: string; // 门店名称

  operator_id: number; // 操作人 ID
  operator_name: string; // 操作人姓名

  create_time: string; // 创建时间
};

// 快捷开卡
export type QuickOpenCardStepKey = 'search' | 'clue' | 'openCard' | 'complete';

export type QuickOpenCardFlowPath = 'existingUser' | 'newClue';

export type OpenCardCompleteResult = {
  payMethod: 'online' | 'offline';
  userId: number;
  userName: string;
  phoneNumber: string;
  memberCardName: string;
  memberCardTypeName: string;
  totalCardNum?: number;
  validDays: number;
  actualAmount: number;
  activateTypeLabel: string;
  issuingStoreName: string;
  issuingAdminName: string;
  orderNo?: string;
};

// 会员数据导入
export type MemberImportResultStatus = 'blocked' | 'confirmable';

export type MemberImportCheckStatus = 'checking' | 'blocked' | 'passed' | 'failed';

export type MemberImportStatus = 'pending' | 'importing' | 'import_success' | 'import_failed';

export type MemberImportFileType = 'origin' | 'error' | 'result';

export type MemberImportErrorItem = {
  row: number;
  name: string;
  phone_number: string;
  type: 'must_fix';
  column: string;
  value?: string;
  reason: string;
  suggestion: string;
};

export type MemberImportConfirmItem = {
  row: number;
  name: string;
  phone_number: string;
  type: 'need_confirm';
  member_card_name: string;
  reason: string;
  suggestion: string;
};

export type MemberImportFileInfo = {
  origin_file_url?: string;
  error_file_url?: string;
  result_file_url?: string;
};

export type MemberImportBaseDataStore = {
  id: number;
  name: string;
};

export type MemberImportBaseDataAdmin = {
  id: number;
  nickname: string;
};

export type MemberImportBaseDataMemberCard = {
  id: number;
  name: string;
  member_card_type_id: number;
  type_name: string;
};

export type MemberImportBaseDataMemberCardType = {
  id: number;
  name: string;
  payment_type: number;
};

export type MemberImportBaseData = {
  stores: MemberImportBaseDataStore[];
  admins: MemberImportBaseDataAdmin[];
  member_cards: MemberImportBaseDataMemberCard[];
  member_card_types: MemberImportBaseDataMemberCardType[];
};

export type MemberImportCheckResult = {
  batch_no: string;
  result_status: MemberImportResultStatus;
  total: number;
  can_import: number;
  must_fix: number;
  need_confirm: number;
  will_create_user: number;
  will_match_user: number;
  will_create_card: number;
  errors: MemberImportErrorItem[];
  confirm_items: MemberImportConfirmItem[];
  file_info?: MemberImportFileInfo;
  base_data?: MemberImportBaseData;
};

export type MemberImportConfirmResult = {
  batch_no: string;
  import_status: MemberImportStatus;
  success_user: number;
  success_card: number;
  skipped: number;
  failed: number;
  errors?: MemberImportErrorItem[];
  file_info?: MemberImportFileInfo;
};

export type MemberImportDetailResult = {
  batch_no: string;
  check_status?: MemberImportCheckStatus;
  import_status?: MemberImportStatus;
  result_status?: MemberImportResultStatus;
  total?: number;
  can_import?: number;
  must_fix?: number;
  need_confirm?: number;
  will_create_user?: number;
  will_match_user?: number;
  will_create_card?: number;
  success_user?: number;
  success_card?: number;
  skipped?: number;
  failed?: number;
  errors?: MemberImportErrorItem[];
  confirm_items?: MemberImportConfirmItem[];
  file_info?: MemberImportFileInfo;
  base_data?: MemberImportBaseData;
};

export type MemberImportFileResult = {
  file_url: string;
};
